import styled from "styled-components";
import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Message, Modal } from "semantic-ui-react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
  clearError,
  createPaymentReverse as settlementCreatePaymentReverse,
} from "../../redux/modules/settlement";
import { getEstate } from "../../redux/modules/estates";
import { CalendarInput } from "../../components/common/calendar-input";
import { FormdataManager } from "../../components/common/formdata";
import { ErrorWrap } from "../../helpers/error";
import { PaymentMethod } from "../../config";
import { t } from "../../constant/translations";
import { Currency } from "../../containers/common/currency";
import dayjs from "dayjs";

const AmountField = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;

  > :first-child {
    flex-grow: 1;
  }
`;

const QuickAmount = styled.div`
  margin: 1.55em 0 1em 1em;

  > button {
    font-size: 1.07em !important;
    margin-right: 0 !important;
  }
`;

export const PayOutAction = ({ estate, triggerText }) => {
  const [formData, setFormData] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const submittedRef = useRef(false);

  const loading = useSelector((state) => state.settlement.pending);
  const error = useSelector((state) =>
    ErrorWrap.fromError(state.settlement.error)
  );
  const dispatch = useDispatch();

  useEffect(() => {
    setFormData({
      estate: estate.id,
      settle_date: dayjs().format("YYYY-MM-DD"),
      payment_method: PaymentMethod.cash,
      deposit_amount: "",
      description: t("estate.deposit_return"),
      auto_settle: true,
      charges: [],
    });
  }, [estate.id]);

  useEffect(() => {
    if (submittedRef.current && !loading && !error.exists()) {
      setModalOpen(false);
      submittedRef.current = false;
    }
  }, [loading, error]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!loading) {
      switch (formData.payment_method) {
        case PaymentMethod.cash:
        case PaymentMethod.bank:
          submittedRef.current = true;
          dispatch(settlementCreatePaymentReverse(formData)).then(
            (resp) => !resp.error && dispatch(getEstate(formData.estate))
          );
          break;
        default:
          break;
      }
    }
  };

  const form = new FormdataManager(formData, setFormData);
  form.withError(error);
  const paymentOptions = [
    {
      text: t(`paymentMethods.${PaymentMethod.cash}`),
      value: PaymentMethod.cash,
    },
    {
      text: t(`paymentMethods.${PaymentMethod.bank}`),
      value: PaymentMethod.bank,
    },
  ];

  return (
    <div>
      <Modal
        onOpen={() => setModalOpen(true)}
        onClose={() => {
          if (!loading) {
            setModalOpen(false);
            dispatch(clearError());
          }
        }}
        open={modalOpen}
        trigger={
          <Button
            basic
            size={"tiny"}
            icon={"undo"}
            content={triggerText}
            title={t("estate.return_deposit")}
          />
        }
        closeIcon={true}
      >
        <Modal.Header>
          {t("estate.deposit_return")} {estate.name}
        </Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <Form onSubmit={handleSubmit} error={error.exists()}>
              <AmountField>
                <Form.Field
                  control="input"
                  name="deposit_amount"
                  label={t("labels.amount")}
                  type="number"
                  step="0.01"
                  fluid={true}
                  autoFocus="autofocus"
                  error={error.has("deposit_amount")}
                  {...form.getAttributes("deposit_amount")}
                />
                <QuickAmount>
                  <Button
                    type={"button"}
                    size={"medium"}
                    color={"green"}
                    onClick={() =>
                      form.setValue(
                        "deposit_amount",
                        Math.max(estate.balance, 0)
                      )
                    }
                    content={<Currency amount={Math.max(estate.balance, 0)} />}
                  />
                </QuickAmount>
              </AmountField>

              <CalendarInput
                label={t("labels.date")}
                {...form.getAttributes("settle_date")}
                toMonth={dayjs().toDate()}
              />
              <Form.Field
                control="textarea"
                label={t("labels.description")}
                rows={3}
                {...form.getAttributes("description")}
              />
              <Form.Select
                label={t("settlement.payment_method")}
                rows={3}
                options={paymentOptions}
                {...form.getAttributes("payment_method")}
              />
              <Message
                error
                content={error.exists() ? error.getGlobal() : t("error.savng")}
              />
              <Button
                type="submit"
                fluid
                size="large"
                positive
                loading={loading}
              >
                {t("estate.pay")}
              </Button>
            </Form>
          </Modal.Description>
        </Modal.Content>
      </Modal>
    </div>
  );
};

PayOutAction.propTypes = {
  estate: PropTypes.object.isRequired,
  triggerText: PropTypes.string,
};

export default PayOutAction;
